import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocFileUploaderItem } from 'commons/components/components-wrapper/poc-file-uploader-item.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { FileSection, FileUploaderComponent, TooltipedTextComponent } from 'commons/components';
import React from 'react';
import { EapSettingsImportFileUploadedLayout } from '../settings-devices-eap-settings-import-modal/settings-devices-eap-settings-import-modal.style';
import { MissingCertificateUploadStatus, UploadFileItem } from './use-eap-import-upload.hook';
import {
    CERTIFICATE_FILE_TYPES,
    getFileExtension,
    getFileUploadDisplayText,
    getFileUploadStatusVariant,
    isValidCertificateFile,
} from './settings-devices-eap-settings-importing.business';
import {
    EapSettingsImportingMissingCertificatesList,
    EapSettingsImportingMissingCertificatesModal,
    EapSettingsImportingUploadedFileName,
} from './settings-devices-eap-settings-importing.style';

type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

type Props = {
    isOpen: boolean;
    missingCertificates: string[];
    missingCertificateUploadStatus: MissingCertificateUploadStatus;
    files: UploadFileItem[];
    isImporting: boolean;
    onLoadFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDismissFile: (fileId: string) => void;
    onContinue: VoidFunction;
    onClose: VoidFunction;
    t: TranslationFunction;
};

export const SettingsDevicesEapSettingsImportingMissingCertificatesModalComponent: React.FC<Props> = ({
    isOpen,
    missingCertificates,
    missingCertificateUploadStatus,
    files,
    isImporting,
    onLoadFile,
    onDismissFile,
    onContinue,
    onClose,
    t,
}) => {
    const uploadedPemNames = new Set(
        files.filter((file) => file.status === 'uploaded' && isValidCertificateFile(file.name)).map((file) => file.name)
    );
    const missingPemFilenames = missingCertificates.filter((name) => !uploadedPemNames.has(name));
    const hasInvalidFiles = files.some((file) => file.status === 'uploaded' && !isValidCertificateFile(file.name));
    const continueDisabled =
        missingCertificateUploadStatus === 'uploading' ||
        missingPemFilenames.length > 0 ||
        hasInvalidFiles ||
        isImporting;

    const uploadedNames = new Set(files.map((file) => file.name));
    const missingPemFilenamesFiltered = missingCertificates.filter((name) => !uploadedNames.has(name));

    return (
        <EapSettingsImportingMissingCertificatesModal
            isOpen={isOpen}
            size="medium"
            title={t('settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.modal.title', {
                count: missingCertificates.length,
            })}
            onClose={onClose}
            footerOptions={
                <PocButton size="medium" onClick={onContinue} disabled={continueDisabled}>
                    {isImporting ? (
                        <PocProgressSpinner size="xSmall" style={{ display: 'block' }} />
                    ) : (
                        t('settings.devices.eap-settings.eap-config-list.action.import.modal.button.continue')
                    )}
                </PocButton>
            }
        >
            <FileSection>
                <FileUploaderComponent
                    title={
                        t(
                            'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.upload.title'
                        ) ?? undefined
                    }
                    buttonText={t(
                        'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.upload.button'
                    )}
                    fileTypes={CERTIFICATE_FILE_TYPES}
                    onLoadFile={onLoadFile}
                    isUploadDisabled={false}
                    multiple
                />
                {missingPemFilenamesFiltered.length > 0 ? (
                    <>
                        <PocTypography variant="body2">
                            {t(
                                'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.upload.missing'
                            )}
                        </PocTypography>
                        <EapSettingsImportingMissingCertificatesList>
                            {missingPemFilenamesFiltered.map((pem) => (
                                <li key={pem}>
                                    <PocTypography variant="body2">{pem}</PocTypography>
                                </li>
                            ))}
                        </EapSettingsImportingMissingCertificatesList>
                    </>
                ) : null}
                <EapSettingsImportFileUploadedLayout>
                    {files.map((file) => {
                        const fileExtension = getFileExtension(file.name);
                        const isValid = CERTIFICATE_FILE_TYPES.includes(fileExtension);
                        const fileStatus = getFileUploadStatusVariant(file.status, isValid);
                        const displayText = getFileUploadDisplayText(file.status, file.progress, isValid, t);

                        return (
                            <PocFileUploaderItem
                                key={file.id}
                                value={file.progress}
                                status={fileStatus}
                                onDismiss={() => onDismissFile(file.id)}
                            >
                                <EapSettingsImportingUploadedFileName slot="prefix">
                                    <TooltipedTextComponent
                                        i18nKey="<tooltip>{{filename}}</tooltip>"
                                        tooltipText={file.name}
                                        i18nValues={{ filename: file.name }}
                                    />
                                </EapSettingsImportingUploadedFileName>
                                <div slot="suffix">{displayText}</div>
                            </PocFileUploaderItem>
                        );
                    })}
                </EapSettingsImportFileUploadedLayout>
            </FileSection>
        </EapSettingsImportingMissingCertificatesModal>
    );
};
